/**
 * 송금 모달 컴포넌트
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TransferModalProps } from "@/features/system/transfer/types/transfer.types";
import { formatAmount } from "@/features/system/transfer/utils/transferUtils";
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import '../styles/TransferModal.css';

const TransferModal: React.FC<TransferModalProps> = ({
                                                         supplierOptions,
                                                         onClose,
                                                         onRequest
                                                     }) => {
    const [selectedSuppId, setSelectedSuppId] = useState('');
    const [amount, setAmount] = useState<string>('');
    const [memo, setMemo] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // 선택된 협력사명 조회
    const selectedSupplierName = supplierOptions.find(
        opt => opt.suppId === selectedSuppId
    )?.suppName || '';

    // 협력사 옵션을 SelectOption 형식으로 변환
    const supplierSelectOptions: SelectOption[] = useMemo(() => {
        return supplierOptions.map(opt => ({
            value: opt.suppId,
            label: opt.suppName
        }));
    }, [supplierOptions]);

    // 금액 유효성 검사
    useEffect(() => {
        setError(null);
        const numAmount = Number(amount.replace(/,/g, ''));

        if (amount && numAmount <= 0) {
            setError('송금 금액은 1원 이상이어야 합니다.');
        }
    }, [amount]);

    // 금액 입력 핸들러 (숫자만 허용)
    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value === '') {
            setAmount('');
        } else {
            const numValue = parseInt(value, 10);
            setAmount(numValue.toLocaleString('ko-KR'));
        }
    };

    // 송금 신청 처리
    const handleSubmit = useCallback(async () => {
        if (!selectedSuppId) {
            setError('협력사를 선택해주세요.');
            return;
        }

        const numAmount = Number(amount.replace(/,/g, ''));
        if (!numAmount || numAmount <= 0) {
            setError('송금 금액을 입력해주세요.');
            return;
        }

        setIsSubmitting(true);
        try {
            await onRequest(selectedSuppId, numAmount, memo || undefined);
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedSuppId, amount, memo, onRequest]);

    // ESC 키로 모달 닫기
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const numAmount = Number(amount.replace(/,/g, '')) || 0;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                {/* 모달 헤더 */}
                <div className="modal-header">
                    <h2 className="modal-title">송금</h2>
                    <button className="modal-close-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* 모달 본문 */}
                <div className="modal-body">
                    <div className="transfer-modal">
                        {/* 협력사 선택 */}
                        <div className="form-group">
                            <label className="form-label required">협력사</label>
                            <SearchableSelect
                                options={supplierSelectOptions}
                                value={selectedSuppId}
                                onChange={setSelectedSuppId}
                                placeholder="협력사 검색..."
                                showAllOption={false}
                            />
                        </div>

                        {/* 송금 금액 입력 */}
                        <div className="form-group">
                            <label className="form-label required">송금 금액</label>
                            <div className="input-with-unit">
                                <input
                                    type="text"
                                    className="form-input"
                                    value={amount}
                                    onChange={handleAmountChange}
                                    placeholder="금액을 입력하세요"
                                />
                                <span className="unit">원</span>
                            </div>
                        </div>

                        {/* 적요 입력 */}
                        <div className="form-group">
                            <label className="form-label">적요</label>
                            <input
                                type="text"
                                className="form-input"
                                value={memo}
                                onChange={(e) => setMemo(e.target.value)}
                                placeholder="적요를 입력하세요 (선택)"
                                maxLength={100}
                            />
                        </div>

                        {/* 송금 정보 미리보기 */}
                        {numAmount > 0 && selectedSuppId && (
                            <div className="preview-section">
                                <div className="preview-title">송금 정보 확인</div>
                                <div className="preview-row">
                                    <span className="preview-label">수신 협력사</span>
                                    <span className="preview-value">{selectedSupplierName}</span>
                                </div>
                                <div className="preview-row">
                                    <span className="preview-label">송금 금액</span>
                                    <span className="preview-value amount-transfer">
                                        {formatAmount(numAmount)}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* 에러 메시지 */}
                        {error && (
                            <div className="error-message">
                                <i className="fas fa-exclamation-circle"></i>
                                {error}
                            </div>
                        )}
                    </div>
                </div>

                {/* 모달 푸터 */}
                <div className="modal-footer">
                    <button
                        className="modal-btn modal-btn-secondary"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        취소
                    </button>
                    <button
                        className="modal-btn modal-btn-primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !!error || !selectedSuppId || !amount}
                    >
                        {isSubmitting ? '처리 중...' : '송금'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TransferModal;
